/**
 * Typed API client wrapper — axios-style surface backed by openapi-fetch.
 *
 * Why both? The legacy `lib/api.js` axios instance is used across 22 pages.
 * Switching the entire codebase at once would be high-risk. Instead this
 * file exposes the same `.get / .post / .patch / .delete` methods that
 * existing pages call, but routes every call through `apiClient` (the
 * generated, schema-aware openapi-fetch client). When the backend OpenAPI
 * changes, regenerating `src/api/schema.d.ts` and pinning a path here
 * surfaces drift at build/dev time.
 *
 * Migration pattern (example: Journal.jsx):
 *   - import api from "../lib/api"     →  import api from "../lib/typedApi"
 *   - api.get("/journal")              →  api.get("/api/journal")
 *   - the rest of the page code is unchanged.
 *
 * The "/api" prefix is required (it matches the path keys in schema.d.ts).
 */
import { apiClient } from "../api/client";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function unwrap({ data, error, response }) {
  if (error) {
    const detail = error?.detail || error?.message || `HTTP ${response?.status || "error"}`;
    const wrapped = new Error(detail);
    // Mirror axios's `err.response.data.detail` shape so existing toast handlers keep working
    wrapped.response = { status: response?.status, data: error };
    throw wrapped;
  }
  return { data, status: response?.status ?? 200, headers: Object.fromEntries(response?.headers?.entries?.() ?? []) };
}

const typedApi = {
  async get(path, opts) {
    return unwrap(await apiClient.GET(path, opts));
  },
  async post(path, body, opts) {
    return unwrap(await apiClient.POST(path, { body, ...opts }));
  },
  async patch(path, body, opts) {
    return unwrap(await apiClient.PATCH(path, { body, ...opts }));
  },
  async delete(path, opts) {
    return unwrap(await apiClient.DELETE(path, opts));
  },
};

export default typedApi;
